import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../ModalShell";
import { PiCheckCircleLight, PiEnvelopeSimpleLight } from "react-icons/pi";
import Button from "../TailwindComponents/Button";
import Textarea from "../TailwindComponents/Textarea";
import { useAppDispatch } from "../../../store";
import { LicensePlan, sendEnterpriseEnquiry } from "../../../store/actions/PlanActions";

/**
 * "Contact us" for a package the team sells directly (PONS Enterprise).
 *
 * Says up front what the team will receive, so the admin is not surprised by
 * a sales email that already knows their seat count: the backend reads the
 * organisation's details itself and emails contact@ and the founders, and
 * sends the admin a copy. All the admin adds is an optional message.
 */
interface EnterpriseEnquiryModalProps {
  open: boolean;
  plan: LicensePlan | null;
  onClose: () => void;
}

const MAX_MESSAGE = 2000;

const EnterpriseEnquiryModal: React.FC<EnterpriseEnquiryModalProps> = ({ open, plan, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ sentTo: string | null } | null>(null);

  useEffect(() => {
    if (open) {
      setMessage("");
      setSent(null);
    }
  }, [open]);

  const planName = plan?.display_name || "PONS Enterprise"; // a product name: not translated

  const handleSend = async () => {
    if (!plan || sending) return;
    setSending(true);
    try {
      const result = await dispatch(sendEnterpriseEnquiry(plan.id, message.trim()) as any);
      if (result) setSent(result);
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      title={t("Contact us about {{plan}}", { plan: planName })}
      icon={<PiEnvelopeSimpleLight />}
      size="md"
    >
              {sent ? (
                <div className="space-y-4" data-testid="enterprise-enquiry-sent">
                  <div className="flex items-start gap-3">
                    <PiCheckCircleLight className="h-6 w-6 shrink-0 text-success-600 dark:text-success-400" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-fg-strong">{t("Your enquiry has been sent")}</p>
                      <p className="text-sm text-fg-body">
                        {t("Our team has your details and will get back to you shortly.")}
                      </p>
                      {sent.sentTo && (
                        <p className="text-xs text-fg-muted">
                          {t("We've sent a copy to {{email}}.", { email: sent.sentTo })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="primary" size="small" onClick={onClose}>
                      {t("Done")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-fg-body">
                    {t("{{plan}} is tailored to each firm. Send us an enquiry and we'll get back to you with an offer.", { plan: planName })}
                  </p>
                  <div className="rounded-md bg-surface-sunken/70 px-3 py-2.5 text-xs text-fg-muted">
                    {t("We'll include your organisation's name, current plan, seats and number of users, so you don't have to type them.")}
                  </div>
                  <Textarea
                    id="enterprise-enquiry-message"
                    label={t("Message")}
                    description={t("Optional")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                    placeholder={t("Anything we should know, such as team size, timing or the features you need")}
                    rows={4}
                    className="resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="small" onClick={onClose} disabled={sending}>
                      {t("Cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={handleSend}
                      isLoading={sending}
                      disabled={sending}
                      icon={<PiEnvelopeSimpleLight />}
                    >
                      {t("Send enquiry")}
                    </Button>
                  </div>
                </div>
              )}
    </ModalShell>
  );
};

export default EnterpriseEnquiryModal;
